import { Box, Grid, SxProps, Theme, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { formatStringCurrency } from "../common/CurrencyFormatter";
import { getDateString } from "../common/RoundHelper";
import CountdownTimer from "../common/CountdownTimer";
import { useMemo } from "react";
import { Button } from "../reusable-components";
import { GetJackpotString, IsLeo, themeC } from "../theme/themehelper";
import { DbetArrowRight } from "../images/dbeticons";
import { ShallowRound } from "../models/round";
import useDetailRound from "../rounds/useDetailRound";
import { getJackpot, getNumFixtures } from "../common/roundhelpers";
import { PrizeGroups } from "../roundview/components/roundinformation";
import SlotCounter from "../common/SlotCounter";

interface RoundBoxContentProps {
    round: ShallowRound;    
    textTitle: string;
    fontWeight: number | string;
    fontFamilyJackpot: string;
    jackpotStyle: SxProps<Theme>;
    smallText: SxProps<Theme>;
    smallTitle: SxProps<Theme>;
    smallTitleUpper: SxProps<Theme>;
    theme: string;
    reportSelection: () => void;
    t: (key: string) => string;
    buttonColor?: string;
    buttonBorderRadius?: string;
    buttonTextColor?: string;
}

function RoundBoxContent({
    round,    
    textTitle,
    fontWeight,
    fontFamilyJackpot,
    jackpotStyle,
    smallText,
    smallTitle,
    smallTitleUpper,
    theme,
    reportSelection,
    t,
    buttonColor,
    buttonBorderRadius,
    buttonTextColor,
}: RoundBoxContentProps) {
    const link = `/rounds/${round.id}`;
    const turnoverAmount = round.operatorTurnover ? Number(round.operatorTurnover.amount) : 'NA';
    const turnoverCurrency = round.operatorTurnover ? round.operatorTurnover.currency : 'NA';
    const dateString = getDateString(round, t);

    const start = new Date(round.openTo);
    const cutoff = new Date(Date.now());
    cutoff.setHours(cutoff.getHours() + 5);
    const showCountDown = start < cutoff;

    const detailRoundResponse = useDetailRound(round.id, {
        refetchInterval: showCountDown ? 30000 : undefined,
    });

    const latestTurnoverAmount = detailRoundResponse.round?.operatorTurnover
        ? Number(detailRoundResponse.round.operatorTurnover.amount)
        : turnoverAmount;

    let jackpot = getJackpot(turnoverCurrency, detailRoundResponse.round);   

    const getRealJackpot = (amount: number | string, roundName: string): number | null => {
        if (typeof amount !== 'number') return null;
        const prizes = PrizeGroups[roundName];
        if (!prizes || prizes.length === 0) return null;
        const topPrize = [...prizes].sort((a, b) => b.correct - a.correct)[0];
        return amount * 0.8 * (topPrize.percent / 100);
    };

    let realJackpot = showCountDown ? getRealJackpot(latestTurnoverAmount, round.name) : null;

    if(theme !== themeC.Kryss){
        realJackpot = null;
    }

    const formattedRealJackpot = useMemo(() => {
        if (realJackpot === null) return "";
        return formatStringCurrency(realJackpot, turnoverCurrency, t, true);
    }, [realJackpot, turnoverCurrency, t]);

    const formattedTurnover = useMemo(() => {
        return formatStringCurrency(latestTurnoverAmount, turnoverCurrency, t, true);
    }, [latestTurnoverAmount, turnoverCurrency, t]);

    return (
        <>
            {jackpot &&
                <Grid container alignItems="center" direction="column" sx={{ marginLeft: "0px" }}>
                    <Typography sx={{
                        fontSize: "14px",
                        color: textTitle,
                        fontWeight: fontWeight,
                        fontFamily: fontFamilyJackpot,
                        marginTop: "10px"
                    }}>{GetJackpotString(window.theme, t, false, getNumFixtures(round.name))}</Typography>
                    <Typography sx={jackpotStyle}>
                        {formatStringCurrency(jackpot, turnoverCurrency, t, true)}
                    </Typography>

                </Grid>
            }


            <Grid container direction="column" justifyContent="space-evenly"
                sx={{ marginTop: "5px", marginBottom: "11px" }}>
                <Grid item>
                    <Grid container alignItems="center" direction="row" justifyContent="space-evenly">
                        <Grid item>
                            <Grid container alignItems="center" direction="column">
                                <Typography sx={smallText}>{t('turnover')}</Typography>
                                <Typography sx={smallTitle}>
                                    <SlotCounter value={formattedTurnover} />
                                </Typography>
                            </Grid>
                        </Grid>
                        {showCountDown && realJackpot !== null &&
                            <Grid item>
                                <Grid container alignItems="center" direction="column">
                                    <Typography sx={smallText}>{GetJackpotString(window.theme, t, true, getNumFixtures(round.name))}</Typography>
                                    <Typography sx={smallTitle}>
                                        <SlotCounter value={formattedRealJackpot} />
                                    </Typography>
                                </Grid>
                            </Grid>
                        }
                    </Grid>
                </Grid>
                <Grid item sx={{ marginTop: "10px" }}>
                    <Grid container alignItems="center" direction="column">
                        <Typography sx={smallText}>{theme === themeC.Kryss ? t('starts_kryss') : t('starts')}</Typography>
                        {
                            showCountDown ?
                                <CountdownTimer width="68px" fontSize="16px" targetTime={new Date(round.openTo).getTime()} /> :
                                <Typography sx={smallTitleUpper}>{dateString}</Typography>
                        }
                    </Grid>
                </Grid>

            </Grid>


            <Grid container direction="row" justifyContent="space-evenly" sx={{ marginTop: "5px", marginBottom: "25px" }}>
                <Grid item>
                    <Link to={link} >
                        <div onClick={() => reportSelection()}>
                            {theme !== themeC.Dbet && !IsLeo(theme) &&
                                <Button>{t('show_round')}</Button>
                            }
                            {theme === themeC.Dbet &&
                                <Button padding="8px 35px">
                                    <Grid container direction="row" alignItems="center">
                                        <Typography sx={{ marginRight: "10px", fontWeight: "700" }}>{t('show_round')}</Typography>
                                        <Grid>
                                            <Grid container direction="column" justifyContent="center" height="100%">
                                                {DbetArrowRight}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Button>
                            }
                            {(IsLeo(theme)) && !buttonColor &&
                                <Button>{t('show_round')}</Button>
                            }
                            {(IsLeo(theme)) && buttonColor &&
                                <Box sx={{
                                    background: 'linear-gradient(90deg, #B79734 29.33%, #DDB95F 67.79%, #FDEDAC 79.81%, #DDB95F 90.38%)',
                                    borderRadius: buttonBorderRadius || '24px',
                                    padding: '2px',
                                    display: 'inline-block',
                                }}>
                                    <Button color={buttonColor} borderRadius={buttonBorderRadius} textColor={buttonTextColor}>{t('show_round')}</Button>
                                </Box>
                            }
                        </div>
                    </Link>
                </Grid>
            </Grid>
        </>
    );
}

export default RoundBoxContent;
